import { createLightTheme, createDarkTheme } from '@fluentui/react-components';
import type { Theme, BrandVariants } from '@fluentui/react-components';
import { htsFont, htsEditorSurface, htsEditorSurfaceLight } from './htsTokens';

/**
 * HTS Brand Colors
 * Canonical references:
 * - hts-platform/HTS-Unified-Brand-Guide.md
 * - hts-platform/docs/brand-center/external-guide/
 *
 * Primary: HTS Blue #0D2152
 * Accent:  HTS Aqua #00B8EB
 *
 * BrandVariants ramp generated centered on #0D2152
 */
const htsBrandColors: BrandVariants = {
  10: '#E3E8F3',
  20: '#C5D0EA',
  30: '#9DB0DA',
  40: '#7490CB',
  50: '#4B71BC',
  60: '#38569F',
  70: '#274187',
  80: '#1D3671',
  90: '#142C62',
  100: '#0D2152', // HTS Blue (primary)
  110: '#0A1A48',
  120: '#08143C',
  130: '#050F31',
  140: '#030925',
  150: '#01051A',
  160: '#00020E',
};

// ---------------------------------------------------------------------------
// Fluent Button variant → contract Button variant mapping
// ---------------------------------------------------------------------------
// The cross-hub Button contract (`controlled-chaos/notes/button-contract.md`)
// defines 7 variants: primary, secondary, danger, success, ghost, accent, link.
// Fluent UI 9 only ships 4: primary, subtle, outline, transparent. This hub
// uses Fluent as its component library, so the contract's 7 → 4 mapping is:
//   contract primary        → Fluent appearance="primary"      (filled navy CTA)
//   contract secondary      → Fluent appearance="outline"      (border + transparent bg)
//   contract ghost          → Fluent appearance="subtle"       (hover only, no border)
//   contract ghost (no bg)  → Fluent appearance="transparent"
//   contract danger         → Fluent appearance="primary" + className override
//                             (Fluent has no native danger — applied per-instance)
//   contract success        → Fluent appearance="primary" + className override
//   contract accent         → Fluent appearance="primary" + className override
//                             (the aqua fill — Fluent's primary uses the brand ramp
//                             so we either swap colorBrandBackground or accept navy)
// The end-user visual outcome is the same as the 7-variant contract; the API
// surface is Fluent's. Documented 2026-07-29 on `button-align-test`.
// ---------------------------------------------------------------------------

/**
 * HTS Light Theme
 * Extends Fluent UI's light theme with HTS brand colors and typography.
 * Overriding fontFamilyBase propagates Inter to every Fluent component
 * (Text, Button, Input, Title, etc.) app-wide via the FluentProvider.
 */
export const htsTheme: Theme = {
  ...createLightTheme(htsBrandColors),
  fontFamilyBase: htsFont.body,
};

/**
 * HTS Dark Theme (app-shell standard dark palette)
 * Project Portal's dark palette is the color reference: base #0A0D13, raised
 * #12161F, overlay #161B26, hover #1A2030; text #E7EAF0 / #9BA3B2 / #7B8494;
 * borders #1B2230 / #262E3D; accent #38C7F0. Applied by the root
 * FluentProvider when the resolved preference is dark — every Fluent surface
 * (popovers, menus, dialogs, drawers) adapts automatically, so no component
 * may hardcode white backgrounds.
 */
export const htsDarkTheme: Theme = {
  ...createDarkTheme(htsBrandColors),
  fontFamilyBase: htsFont.body,
  colorNeutralBackground1: '#12161F', // raised (cards, panels)
  colorNeutralBackground1Hover: '#1A2030',
  colorNeutralBackground1Pressed: '#161B26',
  colorNeutralBackground2: '#0A0D13', // base (page background)
  colorNeutralBackground2Hover: '#12161F',
  colorNeutralBackground2Pressed: '#12161F',
  colorNeutralBackground3: '#161B26', // overlay (popovers, dropdowns)
  colorNeutralBackground4: '#0A0D13',
  colorNeutralBackground5: '#12161F',
  colorNeutralForeground1: '#E7EAF0',
  colorNeutralForeground2: '#9BA3B2',
  colorNeutralForeground3: '#7B8494',
  colorNeutralStroke1: '#262E3D',
  colorNeutralStroke2: '#1B2230',
  colorBrandForeground1: '#38C7F0',
  colorBrandForeground2: '#38C7F0',
};

/**
 * HTS Dark Editor Theme
 * A navy-tinted dark theme for the tool-first plan editor. Built on Fluent's
 * dark theme (so every component — buttons, dropdowns, dialogs — adapts to
 * light-on-dark automatically) with the key surface tokens remapped to the
 * HTS editor surfaces so the chrome reads navy rather than neutral gray.
 * Applied to the canvas tab only, via a nested FluentProvider.
 */
export const htsDarkEditorTheme: Theme = {
  ...createDarkTheme(htsBrandColors),
  fontFamilyBase: htsFont.body,
  colorNeutralBackground1: htsEditorSurface.bg,
  colorNeutralBackground1Hover: htsEditorSurface.raised,
  colorNeutralBackground1Pressed: htsEditorSurface.raised,
  colorNeutralBackground2: htsEditorSurface.toolbar,
  colorNeutralBackground3: htsEditorSurface.raised,
  colorNeutralBackground4: htsEditorSurface.bg,
  colorNeutralStroke1: htsEditorSurface.hairline,
  colorNeutralStroke2: htsEditorSurface.hairline,
};

/**
 * HTS Light Editor Theme — the same editor chrome in the light appearance.
 * The canvas sidebar (device palette) and action bar previously stayed dark
 * in both appearances because the canvas FluentProvider was pinned to the
 * dark editor theme; the page now picks per resolved theme, so both share
 * this identical token remap over Fluent's light base.
 */
export const htsLightEditorTheme: Theme = {
  ...createLightTheme(htsBrandColors),
  fontFamilyBase: htsFont.body,
  colorNeutralBackground1: htsEditorSurfaceLight.bg,
  colorNeutralBackground1Hover: htsEditorSurfaceLight.raised,
  colorNeutralBackground1Pressed: htsEditorSurfaceLight.raised,
  colorNeutralBackground2: htsEditorSurfaceLight.toolbar,
  colorNeutralBackground3: htsEditorSurfaceLight.raised,
  colorNeutralBackground4: htsEditorSurfaceLight.bg,
  colorNeutralStroke1: htsEditorSurfaceLight.hairline,
  colorNeutralStroke2: htsEditorSurfaceLight.hairline,
};

/**
 * HTS Color Palette
 * Official brand guide colors
 */
export const htsColors = {
  navy: '#0D2152',    // HTS Blue (primary brand color)
  blue: '#00B8EB',    // HTS Aqua (accent color)
  text: '#4A4A4A',
  lightGray: '#F1F4F9', // --hts-light-bg per HTS-Unified-Brand-Guide
  white: '#FFFFFF',
} as const;

/**
 * HTS Spacing & Borders
 */
export const htsSpacing = {
  radiusSm: '4px',
  radiusLg: '12px',
  shadow: '0 10px 30px rgba(13, 33, 82, 0.08)',
} as const;

/**
 * HTS Logo URLs
 *
 * Usage guidelines:
 * - primary: Dark blue logo - use only on light or white backgrounds
 * - white: White logo - use on dark backgrounds (navy footers, gradient banners)
 * - icon: Favicon/icon - for smaller UI elements
 * - black: Black logo - alternative for light backgrounds
 */
export const htsLogos = {
  // Local brand assets — the previous Webflow CDN URLs were an external
  // dependency for core UI branding (same failure class as the MC blob outage).
  primary: '/images/HTS-NOBKGRND-BLU.png',
  white: '/images/HTS-WHITE.png',
  icon: '/images/HTS-ICON.png',
  black: '/images/HTS-BLACK.png',
} as const;
